/**
 * WhitelistScreen — gestione contatti pre-approvati (whitelist) = "Rubrica".
 * Mostra chi può scriverti senza richiesta; permette di rimuoverli, scriverci
 * con un tap, e ora di ORDINARE (A→Z / Z→A) e FILTRARE (ricerca per sigil).
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Frame, Header, Footer, Btn, Box, Cursor } from '../components/Frame';
import { auth, onAuthStateChanged } from '../lib/firebase';
import { getMyWhitelist, removeWhitelist, addToWhitelist } from '../lib/messaging';

export default function WhitelistScreen() {
  const navigate = useNavigate();
  const [list, setList] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(null);
  const [addSigil, setAddSigil] = useState('');
  const [addBusy, setAddBusy] = useState(false);
  const [q, setQ] = useState('');          // filtro ricerca
  const [sortDir, setSortDir] = useState('az'); // 'az' | 'za'
  const uidRef = useRef(null);

  const load = useCallback(async () => {
    const uid = uidRef.current;
    if (!uid) { setList([]); return; }
    try { setList(await getMyWhitelist(uid)); setErr(null); }
    catch (e) { setErr(e?.message || 'errore'); setList([]); }
  }, []);

  // L'uid viene da Firebase Auth (sopravvive al reload, a differenza dello store).
  useEffect(() => {
    const unsub = onAuthStateChanged(auth(), (u) => {
      uidRef.current = u?.uid || null;
      load();
    });
    return unsub;
  }, [load]);

  async function remove(contactUid) {
    setBusy(contactUid);
    try { await removeWhitelist(contactUid); await load(); }
    catch (e) { setErr(e?.message || 'errore'); }
    finally { setBusy(null); }
  }

  // Tap su un contatto → "nuovo messaggio" già puntato su di lui.
  function message(sigil) {
    try { sessionStorage.setItem('nullum.connectTo', sigil); } catch {}
    navigate('/new');
  }

  async function add() {
    const s = addSigil.trim().replace(/^@/, '');
    if (!/^[a-zA-Z0-9_]{3,20}$/.test(s)) { setErr('sigil non valido (3-20 caratteri)'); return; }
    setAddBusy(true); setErr(null);
    try {
      await addToWhitelist(s, 'full');
      setAddSigil('');
      await load();
    } catch (e) {
      const m = String(e?.message || '');
      if (m.includes('SIGIL_NOT_FOUND')) setErr('sigil non trovato (deve esistere e, se privato, dovrai conoscerlo esatto)');
      else if (m.includes('CANNOT_ADD_SELF')) setErr('non puoi aggiungere te stesso');
      else setErr(m || 'errore');
    } finally { setAddBusy(false); }
  }

  // Lista filtrata + ordinata.
  const shown = (list || [])
    .filter((e) => {
      const needle = q.trim().toLowerCase().replace(/^@/, '');
      return !needle || (e.sigil || '').toLowerCase().includes(needle);
    })
    .sort((a, b) => {
      const r = (a.sigil || '').localeCompare(b.sigil || '');
      return sortDir === 'az' ? r : -r;
    });

  return (
    <Frame statusLeft="WHITELIST" statusRight="5G">
      <Header title="// Rubrica" crumb="◀ inbox" onCrumbClick={() => navigate('/inbox')} />

      <p className="text-[14px] text-ink-dim mb-3">
        i tuoi contatti pre-approvati: tocca un nome per scrivergli. chi è qui può scriverti senza richiesta.
      </p>

      {err && <Box variant="danger" className="text-[14px] mb-3">errore: {err}</Box>}

      <div className="flex gap-2 mb-3">
        <input
          type="text" value={addSigil} onChange={(e) => setAddSigil(e.target.value)}
          placeholder="@sigil da aggiungere" maxLength={21}
          name="sigil"
          className="flex-1 bg-black border border-ink-dim rounded-lg text-ink font-mono text-[16px] px-3 py-2 focus:border-ink focus:outline-none"
          onKeyDown={(e) => { if (e.key === 'Enter') add(); }}
        />
        <button
          onClick={add} disabled={addBusy || !addSigil.trim()}
          className="shrink-0 border border-ink text-ink hover:bg-ink hover:text-black rounded-lg font-mono text-[14px] px-3 py-2 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {addBusy ? '…' : '+ Aggiungi'}
        </button>
      </div>

      {/* Ordina + filtra (solo se c'è almeno un contatto). */}
      {Array.isArray(list) && list.length > 0 && (
        <div className="flex gap-2 mb-3">
          <input
            type="text" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="🔎 filtra per sigil"
            className="flex-1 bg-black border border-ink-dim/60 text-ink font-mono text-[15px] px-3 py-2 focus:border-ink focus:outline-none"
          />
          <button
            onClick={() => setSortDir((d) => (d === 'az' ? 'za' : 'az'))}
            title="ordina"
            className="shrink-0 border border-ink-dim/60 text-ink-dim hover:text-ink hover:border-ink rounded-lg px-3 text-[14px]"
          >
            {sortDir === 'az' ? 'A→Z' : 'Z→A'}
          </button>
        </div>
      )}

      <div className="flex-1">
        {list === null ? (
          <div className="text-ink-dim text-[14px] py-6 text-center">caricamento<Cursor /></div>
        ) : list.length === 0 ? (
          <div className="text-ink-dim text-[14px] py-8 text-center">nessun contatto in rubrica</div>
        ) : shown.length === 0 ? (
          <div className="text-ink-dim text-[14px] py-8 text-center">nessun contatto per &quot;{q}&quot;</div>
        ) : (
          shown.map((e) => (
            <div key={e.contactUid} className="flex items-center justify-between gap-3 border border-ink-faint p-3 mb-2">
              <button
                onClick={() => e.sigil && message(e.sigil)}
                disabled={!e.sigil}
                className="min-w-0 flex-1 text-left disabled:cursor-default hover:opacity-80 transition-opacity"
                title={e.sigil ? 'scrivi a questo contatto' : ''}
              >
                <div className="text-ink text-[15px] truncate">@{e.sigil || `${e.contactUid.slice(0, 8)}…`}</div>
                <div className="text-ink-dim text-[12px]">
                  {e.level || 'request'}{e.viaInvite ? ' · via invito' : ''}{e.sigil ? ' · tap per scrivere' : ''}
                </div>
              </button>
              <button
                onClick={() => remove(e.contactUid)}
                disabled={busy === e.contactUid}
                className="shrink-0 border border-red-500/50 text-red-400 hover:bg-red-500/10 text-[13px] px-3 py-2 disabled:opacity-40"
              >
                {busy === e.contactUid ? '…' : 'Rimuovi'}
              </button>
            </div>
          ))
        )}
      </div>

      <Footer>
        <Btn variant="ghost" onClick={() => navigate('/inbox')}>◀ Inbox</Btn>
      </Footer>
    </Frame>
  );
}
